import React from 'react'
import { Slot } from 'radix-ui'

import { cn } from '@/shared/lib/utils'

const InlineEditContext = React.createContext<
	| {
			isEditing: boolean
			setIsEditing: (isEditing: boolean) => void
			onConfirm: (event?: React.KeyboardEvent<HTMLInputElement>) => void
	  }
	| undefined
>(undefined)
const useInlineEdit = () => {
	const context = React.use(InlineEditContext)
	if (!context) {
		throw new Error('useInlineEdit must be used within InlineEditProvider')
	}

	return context
}
export const InlineEdit = ({
	children,
	onConfirm,
}: {
	children: React.ReactNode
	onConfirm: (event?: React.KeyboardEvent<HTMLInputElement>) => void
}) => {
	const [isEditing, setIsEditing] = React.useState(false)

	return (
		<InlineEditContext value={{ isEditing, setIsEditing, onConfirm }}>
			{children}
		</InlineEditContext>
	)
}
InlineEdit.displayName = 'InlineEdit'

export const InlineEditTrigger = ({
	children,
	className,
	...props
}: React.ComponentProps<typeof Slot.Root>) => {
	const { isEditing, setIsEditing } = useInlineEdit()

	if (isEditing) {
		return null
	}

	return (
		<Slot.Root
			{...props}
			className={cn('cursor-pointer', className)}
			onClick={() => setIsEditing(true)}
			{...props}
		>
			{children}
		</Slot.Root>
	)
}
InlineEditTrigger.displayName = 'InlineEditTrigger'

export const InlineEditInput = ({
	children,
	...props
}: React.ComponentProps<typeof Slot.Root>) => {
	const { isEditing, onConfirm, setIsEditing } = useInlineEdit()
	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.keyCode === 13) {
			onConfirm?.()
			setIsEditing(false)
		}
	}
	if (!isEditing) {
		return null
	}

	return (
		<Slot.Root
			{...props}
			onKeyDown={handleKeyDown}
			onBlur={() => {
				onConfirm?.()
				setIsEditing(false)
			}}
		>
			{children}
		</Slot.Root>
	)
}
InlineEditInput.displayName = 'InlineEditInput'
